import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, Euro, ArrowRight, CheckCircle2, 
  FileText, HelpCircle, TrendingUp, Globe, MapPin
} from 'lucide-react';
import { motion } from 'framer-motion';
import CryptoTaxCalculatorTool from '../components/tools/stage1/CryptoTaxCalculatorTool';
import { TaxPageMonetization, TaxReportEmailCapture } from '../components/TaxPageMonetization';

const CryptoTaxCalculatorEurope = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How is cryptocurrency taxed in Europe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Crypto taxation varies significantly across European countries. Germany offers tax-free gains after 1 year holding. Portugal taxes short-term gains at 28%. France applies a flat 30% rate. Bulgaria has a flat 10% rate. Always check your specific country's regulations."
        }
      },
      {
        "@type": "Question",
        "name": "Which European country has the lowest crypto tax?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Germany offers 0% tax on crypto held over 1 year. Portugal exempts long-term holdings from tax. Bulgaria has a flat 10% rate. Malta, Monaco, and Switzerland also offer favorable crypto tax treatment in certain circumstances."
        }
      },
      {
        "@type": "Question",
        "name": "Is crypto taxable in Germany?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, but with a significant benefit: crypto held for more than 1 year is completely tax-free in Germany. Short-term gains (under 1 year) are taxed at your personal income tax rate (up to 45%). There's also a €600 annual exemption."
        }
      },
      {
        "@type": "Question",
        "name": "How does the EU regulate crypto taxes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The EU doesn't have unified crypto tax rules - each member state sets its own. However, DAC8 directive requires exchanges to report user transactions to tax authorities. MiCA regulation focuses on consumer protection but not taxation directly."
        }
      }
    ]
  };

  const countryRates = [
    { country: 'Germany', rate: '0%*', note: '*After 1 year hold', color: 'green' },
    { country: 'Bulgaria', rate: '10%', note: 'Flat rate', color: 'blue' },
    { country: 'Portugal', rate: '28%', note: 'Short-term only', color: 'amber' },
    { country: 'France', rate: '30%', note: 'Flat tax', color: 'purple' },
    { country: 'Italy', rate: '26%', note: 'Capital gains', color: 'red' },
    { country: 'Spain', rate: '19-28%', note: 'Progressive', color: 'orange' }
  ];

  return (
    <>
      <SEOHead
        title="Crypto Tax Calculator Europe (2026) - EU Capital Gains Tax Rates"
        description="Calculate European cryptocurrency taxes. Compare crypto tax rates in Germany (0% after 1yr), France (30%), Portugal, Italy. Free EU crypto tax calculator updated for 2026."
        keywords="crypto tax calculator Europe, EU cryptocurrency tax, Germany crypto tax, Portugal crypto tax, European crypto regulations, crypto capital gains EU"
        canonical="https://toolsforgeapp.com/crypto-tax-calculator-europe"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen py-12 px-6" data-testid="crypto-tax-europe-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/crypto-calculators" className="hover:text-primary transition-colors">Crypto Calculators</Link>
            <span>/</span>
            <span className="text-foreground">Europe Tax Calculator</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-blue-500/30 text-blue-400">
              <Globe className="w-3 h-3 mr-2" />
              EU Rates Updated 2026
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Crypto Tax Calculator Europe
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Navigate European cryptocurrency taxation with our comprehensive calculator. Tax rules vary dramatically across the EU—from Germany's tax-free long-term holdings to France's flat 30% rate. Our calculator supports multiple European countries and helps you estimate your crypto tax liability accurately based on 2026 regulations.
            </p>
          </motion.div>

          {/* Country Rates Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {countryRates.map(({ country, rate, note, color }) => (
              <div key={country} className={`p-3 rounded-xl border border-${color}-500/30 bg-${color}-500/10`}>
                <div className="flex items-center gap-1 mb-1">
                  <MapPin className={`w-3 h-3 text-${color}-400`} />
                  <span className="text-xs font-medium">{country}</span>
                </div>
                <p className={`text-xl font-bold text-${color}-400`}>{rate}</p>
                <p className="text-xs text-muted-foreground">{note}</p>
              </div>
            ))}
          </div>

          {/* Calculator Tool */}
          <div className="mb-8">
            <CryptoTaxCalculatorTool />
          </div>

          {/* Monetization Section - After Calculator */}
          <TaxPageMonetization 
            region="europe"
            complianceText="Meet DAC8 reporting requirements across the EU"
          />

          {/* How to Calculate Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Calculator className="w-6 h-6 text-primary" />
              How Crypto Taxes Work in Europe
            </h2>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground mb-4">
                Unlike the US, there's no unified European crypto tax policy. Each EU member state has its own rules. Here's what you need to know:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-5 rounded-xl border border-green-500/30 bg-green-500/5">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-400">
                    <CheckCircle2 className="w-5 h-5" />
                    Tax-Favorable Countries
                  </h3>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li><strong>Germany:</strong> 0% tax after 1 year holding period</li>
                    <li><strong>Portugal:</strong> Long-term holdings often tax-free</li>
                    <li><strong>Switzerland:</strong> No capital gains tax for individuals</li>
                    <li><strong>Malta:</strong> Favorable treatment for long-term investors</li>
                  </ul>
                </div>
                
                <div className="p-5 rounded-xl border border-amber-500/30 bg-amber-500/5">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-amber-400">
                    <Euro className="w-5 h-5" />
                    Flat Rate Countries
                  </h3>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li><strong>Bulgaria:</strong> Simple 10% flat rate</li>
                    <li><strong>France:</strong> 30% flat tax (PFU)</li>
                    <li><strong>Italy:</strong> 26% capital gains tax</li>
                    <li><strong>Poland:</strong> 19% flat rate</li>
                  </ul>
                </div>
              </div>
              
              <div className="p-5 rounded-xl border border-blue-500/30 bg-blue-500/5 mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-400">
                  <Globe className="w-5 h-5" />
                  EU-Wide Regulations (DAC8 & MiCA)
                </h3>
                <p className="text-sm text-muted-foreground">
                  The EU's DAC8 directive requires crypto exchanges to report user transactions to tax authorities starting 2026. MiCA (Markets in Crypto-Assets) provides consumer protection but doesn't unify taxation. Always verify with your local tax authority.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Example Calculation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 p-6 rounded-xl border border-border bg-card"
          >
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Example: German Crypto Tax Calculation
            </h2>
            
            <div className="bg-gray-900/50 rounded-lg p-4 font-mono text-sm">
              <p className="text-muted-foreground mb-2">// Scenario: Selling Bitcoin in Germany after 2 years</p>
              <p className="text-blue-400">Purchase Date: March 2024</p>
              <p className="text-blue-400">Sale Date: March 2026 (held 2 years)</p>
              <p className="text-blue-400">Purchase Price: €25,000</p>
              <p className="text-blue-400">Sale Price: €60,000</p>
              <p className="text-muted-foreground mt-2">// Calculation</p>
              <p className="text-white">Capital Gain: €60,000 - €25,000 = <span className="text-green-400">€35,000</span></p>
              <p className="text-white">Holding Period: <span className="text-yellow-400">Over 1 year</span></p>
              <p className="text-white">Tax Rate (Germany, over 1yr): <span className="text-green-400">0%</span></p>
              <p className="text-white mt-2 text-lg">Tax Owed: €35,000 × 0% = <span className="text-green-400 font-bold">€0 (Tax-Free!)</span></p>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3">
              * Germany's 1-year holding rule makes it one of the most crypto-friendly EU countries for long-term investors.
            </p>
          </motion.section>

          {/* Email Capture - Before FAQ */}
          <TaxReportEmailCapture region="europe" />

          {/* FAQ Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-purple-400" />
              European Crypto Tax FAQs
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How is cryptocurrency taxed in Europe?",
                  a: "It varies by country. Germany offers 0% tax after 1 year holding. France has a flat 30% rate. Bulgaria charges 10%. Portugal exempts long-term holdings. Italy and Spain have progressive rates. There's no unified EU crypto tax policy."
                },
                {
                  q: "Which European country has the lowest crypto tax?",
                  a: "Germany offers 0% tax on crypto held over 1 year, making it excellent for HODLers. Portugal has historically been crypto-friendly though rules are tightening. Bulgaria's flat 10% rate is among the lowest in the EU."
                },
                {
                  q: "Is crypto taxable in Germany?",
                  a: "Yes, but with a major benefit: crypto held over 1 year is completely tax-free. For holdings under 1 year, gains are taxed at your personal income tax rate (up to 45%). There's also a €600 annual exemption for short-term gains."
                },
                {
                  q: "What is DAC8 and how does it affect European crypto users?",
                  a: "DAC8 (Directive on Administrative Cooperation 8) requires crypto asset service providers to report user transactions to EU tax authorities. This increases transparency and makes it harder to evade taxes. It takes effect in 2026."
                },
                {
                  q: "Do I need to pay taxes on crypto in Portugal?",
                  a: "Portugal taxes short-term crypto gains (held under 1 year) at 28%. Long-term holdings may be exempt, but rules have been evolving. Professional traders face different treatment. Always consult a Portuguese tax advisor."
                }
              ].map((faq, index) => (
                <div key={index} className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Related Tools */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">More Crypto Calculators</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'US Tax Calculator', slug: '../crypto-tax-calculator-usa', isPage: true },
                { name: 'UK Tax Calculator', slug: '../crypto-tax-calculator-uk', isPage: true },
                { name: 'Crypto Profit Calculator', slug: 'crypto-profit-calculator' },
                { name: 'All Crypto Tools', slug: '../crypto-calculators', isHub: true }
              ].map(tool => (
                <Link
                  key={tool.slug}
                  to={tool.isPage ? `/${tool.slug.replace('../', '')}` : tool.isHub ? '/crypto-calculators' : `/tools/${tool.slug}`}
                  className="p-3 rounded-lg border border-border bg-card hover:border-primary/50 transition-all text-sm font-medium flex items-center justify-between"
                >
                  {tool.name}
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default CryptoTaxCalculatorEurope;
